import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils.ts';
import { Card, CardContent } from '../../elements/ui/card';
import { AnimatedNumber, busyDimClass, RevealCard } from '../../elements/ui/motion';
import { Skeleton } from '../../elements/ui/skeleton';
import { PigletRegistrationDashboard, getPigletRegistrationTotals } from '../../models/piglet-registration';
import { pigletRegistrationColors } from './colors';

const num = (value: number) => value.toLocaleString();
const pct = (value: number) => `${Math.round(value)}%`;

interface StatCardProps {
  label: string;
  value: number;
  caption: string;
  dotColor: string;
  formatValue?: (value: number) => string;
  delayMs?: number;
}

const StatCard = ({ label, value, caption, dotColor, formatValue = num, delayMs = 0 }: StatCardProps) => (
  <RevealCard delayMs={delayMs} className="h-full">
    {(revealed) => (
      <CardContent className="p-5">
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900 tabular-nums">
          <AnimatedNumber value={value} format={formatValue} active={revealed} delayMs={delayMs + 100} />
        </p>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: dotColor }} />
          {caption}
        </p>
      </CardContent>
    )}
  </RevealCard>
);

/**
 * The two skeletons mirror their cards line for line - same paddings, same line heights, same gaps - so
 * the layout does not shift when the data lands. The split card is the tallest of the four and sets the
 * row height, which is why it needs its own shape rather than a third copy of the stat one.
 */
const StatCardSkeleton = () => (
  <Card className="h-full">
    <CardContent className="p-5">
      <Skeleton className="h-4 w-28" />
      <Skeleton className="mt-2 h-9 w-24" />
      <Skeleton className="mt-2 h-4 w-36" />
    </CardContent>
  </Card>
);

const WeightSplitCardSkeleton = () => (
  <Card className="h-full">
    <CardContent className="p-5">
      <Skeleton className="h-4 w-28" />
      <Skeleton className="mt-3 h-2 w-full rounded-full" />

      <div className="mt-3 grid gap-1">
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-4 w-full" />
        ))}
      </div>

      <Skeleton className="mt-3 h-4 w-32" />
    </CardContent>
  </Card>
);

interface WeightSplitCardProps {
  light: number;
  medium: number;
  heavy: number;
  classified: number;
  delayMs?: number;
}

/**
 * The three weight classes share one card because they are one number split three ways - three separate
 * cards would invite reading them as unrelated metrics. The stacked bar carries the shape at a glance and
 * the rows carry the values; the widths use the raw percentages so rounding never makes the bar overflow.
 *
 * Exported because the admin farms overview shows the same split across every farm. Same metric, same card:
 * an admin comparing the estate against one farm should not have to translate between two presentations.
 */
export const WeightSplitCard = ({ light, medium, heavy, classified, delayMs = 0 }: WeightSplitCardProps) => {
  const { t } = useTranslation();

  // Both the bar and the rows are driven by the *rounded* percentage, so the two can never disagree.
  // Sizing the bar off the raw value instead is what produced the stray nub at the left cap: a class
  // holding 1 piglet in 250 is 0.4%, which the row rounds to "0%" but the bar still drew ~3px wide.
  const classes = [
    {
      key: 'light',
      label: t('f-piglets-reg-class-light'),
      percent: Math.round(light),
      color: pigletRegistrationColors.weightClass.light
    },
    {
      key: 'medium',
      label: t('f-piglets-reg-class-medium'),
      percent: Math.round(medium),
      color: pigletRegistrationColors.weightClass.medium
    },
    {
      key: 'heavy',
      label: t('f-piglets-reg-class-heavy'),
      percent: Math.round(heavy),
      color: pigletRegistrationColors.weightClass.heavy
    }
  ];

  const segments = classes.filter((entry) => entry.percent > 0);

  // Normalise against the segments drawn rather than assuming they total 100: each percentage is rounded
  // independently, so they can add up to 99 or 101, and a dropped class would leave the bar short.
  const segmentTotal = segments.reduce((sum, entry) => sum + entry.percent, 0) || 1;

  return (
    <RevealCard delayMs={delayMs} className="h-full">
      {(revealed) => (
        <CardContent className="p-5">
          <p className="text-xs font-medium text-gray-500">{t('f-piglets-reg-weight-split')}</p>

          {/* A grey track underneath, so a farm with nothing classified yet still shows an empty bar
            rather than a collapsed nothing. */}
          <div className="mt-3 flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
            {segments.map((entry, index) => (
              <div
                key={entry.key}
                className={cn(
                  'motion-safe:transition-[width] motion-safe:duration-700 motion-safe:ease-out',
                  index > 0 && 'ml-0.5'
                )}
                // Grows from nothing on reveal, and slides between splits when the period changes.
                style={{
                  width: revealed ? `${(entry.percent / segmentTotal) * 100}%` : '0%',
                  backgroundColor: entry.color,
                  transitionDelay: `${delayMs + 100}ms`
                }}
              />
            ))}
          </div>

          <div className="mt-3 grid gap-1">
            {classes.map((entry) => (
              <div key={entry.key} className="flex items-center justify-between gap-4 text-xs">
                <span className="flex items-center gap-1.5 text-gray-500">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                  {entry.label}
                </span>
                <span className="font-semibold tabular-nums text-gray-900">{`${entry.percent}%`}</span>
              </div>
            ))}
          </div>

          <p className="mt-3 text-xs text-gray-500">
            {t('f-piglets-reg-weight-split-caption', { classified: num(classified) })}
          </p>
        </CardContent>
      )}
    </RevealCard>
  );
};

export interface PigletStatCardsProps {
  dashboard?: PigletRegistrationDashboard | null;
  isBusy: boolean;
}

export default function PigletStatCards({ dashboard, isBusy }: PigletStatCardsProps) {
  const { t } = useTranslation();

  // Skeletons only until the first answer: after that a period change dims the figures it is about to
  // replace, so the cards stay mounted and count across to the new values instead of from zero.
  if (!dashboard) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCardSkeleton />
        <StatCardSkeleton />
        <StatCardSkeleton />
        <WeightSplitCardSkeleton />
      </div>
    );
  }

  const totals = getPigletRegistrationTotals(dashboard);

  return (
    <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4', busyDimClass(isBusy))}>
      {/* The caption carries the shortfall that the dropped "missing vs expected" card used to show. The
          headline counts the whole period, the caption only the completed cycles: the cycle in progress has
          no verdict yet, so its piglets are counted but not held against a target. */}
      <StatCard
        label={t('f-piglets-reg-registered')}
        value={dashboard.totalRegistered}
        caption={t('f-piglets-reg-registered-caption-cycles', {
          registered: num(dashboard.registeredInCompletedCycles),
          expected: num(dashboard.totalExpected),
          percent: Math.round(dashboard.registeredPercentOfExpected)
        })}
        dotColor={pigletRegistrationColors.neutral}
      />

      {/* The caption names the weighed count rather than the total: a farm that weighs a third of its
          piglets gets an average built mostly from weight-category estimates, and should see that. */}
      <StatCard
        label={t('f-piglets-reg-birth-weight')}
        delayMs={70}
        value={dashboard.averageBirthWeightGrams}
        formatValue={(value) => t('f-piglets-reg-grams', { value: num(value) })}
        caption={t('f-piglets-reg-birth-weight-caption', {
          weighed: num(dashboard.weighedCount),
          registered: num(dashboard.totalRegistered)
        })}
        dotColor={pigletRegistrationColors.target}
      />

      <StatCard
        label={t('f-piglets-reg-mortality')}
        delayMs={140}
        value={Math.round(totals.mortalityPercent)}
        formatValue={pct}
        caption={t('f-piglets-reg-mortality-caption', {
          dead: num(totals.deadTotal),
          registered: num(dashboard.totalRegistered)
        })}
        dotColor={pigletRegistrationColors.belowExpected}
      />

      <WeightSplitCard
        light={dashboard.lightPercent}
        medium={dashboard.mediumPercent}
        heavy={dashboard.heavyPercent}
        classified={dashboard.classifiedCount}
        delayMs={210}
      />
    </div>
  );
}
